import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { getIcon } from './Icons';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import MaintenanceBanner from './MaintenanceBanner';
import './AdminLayout.css';

const menuItemsConfig = [
  { icon: 'dashboard', labelKey: 'admin_dashboard', path: '/admin' },
  { icon: 'orders', labelKey: 'admin_orders', path: '/admin/orders' },
  {
    icon: 'products',
    labelKey: 'admin_assortment',
    isGroup: true,
    items: [
      { icon: 'products', labelKey: 'admin_products', path: '/admin/products' },
      { icon: 'categories', labelKey: 'admin_categories', path: '/admin/categories' },
    ]
  },
  { icon: 'usersStat', labelKey: 'admin_users', path: '/admin/users' },
  {
    icon: 'finance',
    labelKey: 'admin_finance',
    isGroup: true,
    items: [
      { icon: 'invoices', labelKey: 'admin_invoices', path: '/admin/invoices' },
      { icon: 'invoices', labelKey: 'admin_credit_invoices', path: '/admin/credit-invoices' },
      { icon: 'storeCredits', labelKey: 'admin_store_credits', path: '/admin/store-credits' },
    ]
  },
  {
    icon: 'promotions',
    labelKey: 'admin_promotions_nav',
    isGroup: true,
    items: [
      { icon: 'promotions', labelKey: 'admin_promotions', path: '/admin/promotions' },
      { icon: 'coupons', labelKey: 'admin_coupons', path: '/admin/coupons' },
      { icon: 'giftCards', labelKey: 'admin_gift_cards', path: '/admin/gift-cards' },
      { icon: 'loyalty', labelKey: 'admin_loyalty', path: '/admin/loyalty' },
    ]
  },
  {
    icon: 'pages',
    labelKey: 'admin_content',
    isGroup: true,
    items: [
      { icon: 'pages', labelKey: 'admin_header_menu', path: '/admin/header-menu' },
      { icon: 'pages', labelKey: 'admin_home_page', path: '/admin/home-page-editor' },
      { icon: 'pages', labelKey: 'admin_pages', path: '/admin/pages' },
      { icon: 'pages', labelKey: 'admin_footer_links', path: '/admin/footer-links' },
    ]
  },
  {
    icon: 'settings',
    labelKey: 'admin_settings',
    isGroup: true,
    items: [
      { icon: 'settings', labelKey: 'admin_store', path: '/admin/store-settings' },
      { icon: 'shipping', labelKey: 'admin_shipping_methods', path: '/admin/shipping-methods' },
      { icon: 'shipping', labelKey: 'admin_courier_integrations', path: '/admin/courier-integrations' },
      { icon: 'finance', labelKey: 'admin_finance_reports', path: '/admin/finance-reports' },
      { icon: 'payment', labelKey: 'admin_payment_methods', path: '/admin/payment-methods' },
      { icon: 'invoices', labelKey: 'admin_invoice_settings', path: '/admin/invoice-settings' },
      { icon: 'email', labelKey: 'admin_email_automation', path: '/admin/email-automation' },
      { icon: 'email', labelKey: 'admin_smtp', path: '/admin/smtp-settings' },
      { icon: 'marketplace', labelKey: 'admin_marketplace', path: '/admin/marketplace-integrations' },
      { icon: 'backup', labelKey: 'admin_backups', path: '/admin/backups' },
      { icon: 'error', labelKey: 'admin_errors', path: '/admin/error-logs' },
    ]
  },
];

const quickActionsConfig = [
  { icon: 'add', labelKey: 'admin_new_product', path: '/admin/products?new=true' },
];

function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [storeSettings, setStoreSettings] = useState(null);
  const [authStatus, setAuthStatus] = useState('pending'); // 'pending' | 'allowed' | 'redirect_login' | 'redirect_home'

  // Tikrinti ar vartotojas prisijungęs ir turi admin teises prieš rodant admin zoną
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthStatus('redirect_login');
      return;
    }
    apiClient.get('/auth/me')
      .then((res) => {
        const user = res.data;
        if (user && user.is_admin) {
          setAuthStatus('allowed');
        } else {
          setAuthStatus('redirect_home');
        }
      })
      .catch(() => {
        setAuthStatus('redirect_login');
      });
  }, []);

  const menuItems = React.useMemo(() => menuItemsConfig.map(item => ({
    ...item,
    label: t(item.labelKey),
    ...(item.items && { items: item.items.map(sub => ({ ...sub, label: t(sub.labelKey) })) }),
  })), [t]);

  const quickActions = React.useMemo(() => quickActionsConfig.map(a => ({
    ...a,
    label: t(a.labelKey),
  })), [t]);

  // Load store settings
  useEffect(() => {
    const loadStoreSettings = async () => {
      try {
        const res = await apiClient.get('/store-settings');
        const settings = res.data;
        // Pašalinti "parduotuvė" iš company_name
        if (settings.company_name) {
          settings.company_name = settings.company_name.replace(/\s*parduotuvė\s*/gi, '').trim();
        }
        // Patikrinti ar logo_url yra teisingas
        if (settings.logo_url && settings.logo_url !== null && settings.logo_url !== undefined) {
          if (!settings.logo_url.startsWith('http') && !settings.logo_url.startsWith('/')) {
            settings.logo_url = '/' + settings.logo_url;
          }
        } else {
          settings.logo_url = null;
        }
        setStoreSettings(settings);
      } catch (error) {
        setStoreSettings({
          company_name: 'Admin',
          logo_url: null
        });
      }
    };
    loadStoreSettings();
  }, []);

  // Auto-expand group if current path is inside it
  useEffect(() => {
    const newExpanded = {};
    menuItems.forEach((item, index) => {
      if (item.isGroup && item.items) {
        const isActive = item.items.some(subItem => location.pathname === subItem.path);
        if (isActive) {
          newExpanded[index] = true;
        }
      }
    });
    setExpandedGroups(prev => ({ ...prev, ...newExpanded }));
  }, [location.pathname, menuItems]);

  // Klaviatūra: tik paieškos iškvietimas (⌘K / Ctrl+K) ir Escape uždarymui
  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        return;
      }
      if (e.key === 'Escape') {
        setShowSearch(false);
        setSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      const results = [];
      
      menuItems.forEach(item => {
        if (item.isGroup && item.items) {
          // Search in subitems
          item.items.forEach(subItem => {
            if (subItem.label.toLowerCase().includes(searchLower)) {
              results.push(subItem);
            }
          });
        } else if (item.label.toLowerCase().includes(searchLower)) {
          results.push(item);
        }
      });
      
      quickActions.forEach(action => {
        if (action.label.toLowerCase().includes(searchLower)) {
          results.push(action);
        }
      });
      
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, menuItems, quickActions]);

  if (authStatus === 'redirect_login') {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (authStatus === 'redirect_home') {
    return <Navigate to="/" replace />;
  }
  if (authStatus === 'pending') {
    return (
      <div className="admin-auth-check" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--admin-bg, #f5f5f5)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #eee', borderTopColor: '#333', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ margin: 0, color: '#666' }}>{t('admin_loading') || 'Kraunama...'}</p>
        </div>
      </div>
    );
  }

  const handleSearchSelect = (item) => {
    navigate(item.path);
    setShowSearch(false);
    setSearchQuery('');
  };

  return (
    <div className={`admin-layout${storeSettings?.maintenance_mode ? ' has-maintenance-banner' : ''}`}>
      {storeSettings?.maintenance_mode && <MaintenanceBanner />}
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <Link to="/admin" className="sidebar-logo">
            {storeSettings?.logo_url && 
             storeSettings.logo_url !== null && 
             storeSettings.logo_url !== undefined &&
             String(storeSettings.logo_url).trim() !== '' ? (
              <img 
                src={storeSettings.logo_url} 
                alt={storeSettings.company_name || 'Admin'} 
                className="sidebar-logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  // Rodyti ikoną su tekstu jei logotipas neįkėlė
                  if (!e.target.parentNode.querySelector('.logo-icon')) {
                    const icon = document.createElement('span');
                    icon.className = 'logo-icon';
                    icon.innerHTML = getIcon('settings');
                    const text = document.createElement('span');
                    text.className = 'logo-text';
                    text.textContent = storeSettings?.company_name || 'Admin';
                    e.target.parentNode.appendChild(icon);
                    if (sidebarOpen) {
                      e.target.parentNode.appendChild(text);
                    }
                  }
                }}
              />
            ) : (
              <>
                <span className="logo-icon">{getIcon('settings')}</span>
                {sidebarOpen && <span className="logo-text">{storeSettings?.company_name || 'Admin'}</span>}
              </>
            )}
          </Link>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? t('admin_collapse_sidebar') : t('admin_expand_sidebar')}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Quick Search */}
        <div className="sidebar-search">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder={sidebarOpen ? t('admin_search_placeholder') : t('admin_search_placeholder_short')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSearch(true)}
              className="search-input"
            />
          </div>
          {showSearch && searchQuery && searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSearchSelect(item)}
                  className="search-result-item"
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                  {item.shortcut && (
                    <span className="shortcut">{item.shortcut}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav">
          {menuItems.map((item, index) => {
            if (item.isGroup) {
              const isExpanded = expandedGroups[index];
              const hasActiveSub = item.items.some(subItem => location.pathname === subItem.path);
              
              return (
                <div key={index} className="nav-group">
                  <button
                    className={`nav-item nav-group-toggle ${hasActiveSub ? 'active' : ''}`}
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [index]: !prev[index] }))}
                    title={sidebarOpen ? '' : item.label}
                  >
                    <span className="nav-icon">{getIcon(item.icon)}</span>
                    {sidebarOpen && (
                      <>
                        <span className="nav-label">{item.label}</span>
                        <span className="nav-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                      </>
                    )}
                  </button>
                  {isExpanded && sidebarOpen && (
                    <div className="nav-subitems">
                      {item.items.map((subItem) => {
                        const isActive = location.pathname === subItem.path;
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`nav-subitem ${isActive ? 'active' : ''}`}
                          >
                            <span className="nav-icon">{getIcon(subItem.icon)}</span>
                            <span className="nav-label">{subItem.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            const isActive = location.pathname === item.path || 
                           (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                title={sidebarOpen ? '' : item.label}
              >
                <span className="nav-icon">{getIcon(item.icon)}</span>
                {sidebarOpen && (
                  <>
                    <span className="nav-label">{item.label}</span>
                    {item.shortcut && <span className="nav-shortcut">{item.shortcut}</span>}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Quick Actions */}
        {sidebarOpen && (
          <div className="sidebar-quick-actions">
            <div className="quick-actions-header">{t('admin_quick_actions')}</div>
            {quickActions.map((action) => (
              <Link
                key={action.path}
                to={action.path}
                className="quick-action-item"
              >
                <span className="action-icon">{getIcon(action.icon)}</span>
                <span className="action-label">{action.label}</span>
              </Link>
            ))}
            <div className="quick-actions-lang">
              <span className="quick-actions-lang-label">{t('language')}</span>
              <div className="admin-lang-switcher">
                {['lt', 'en', 'de'].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    className={`admin-lang-btn ${language === lang ? 'active' : ''}`}
                    onClick={() => setLanguage(lang)}
                    title={lang === 'lt' ? 'Lietuvių' : lang === 'en' ? 'English' : 'Deutsch'}
                    aria-label={t('language')}
                  >
                    {lang === 'lt' ? 'LT' : lang === 'en' ? 'EN' : 'DE'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="sidebar-footer">
          <Link to="/" className="sidebar-footer-link">
            <span className="nav-icon">{getIcon('home')}</span>
            {sidebarOpen && <span>{t('admin_back_to_store')}</span>}
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`admin-main ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {children}
      </main>

      {/* Search Overlay */}
      {showSearch && (
        <div 
          className="search-overlay"
          onClick={() => {
            setShowSearch(false);
            setSearchQuery('');
          }}
        >
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-header">
              <span className="search-icon-large">🔍</span>
              <input
                type="text"
                placeholder={t('admin_search_functions')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                className="search-modal-input"
              />
            </div>
            {searchQuery && (
              <div className="search-modal-results">
                {searchResults.length > 0 ? (
                  searchResults.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSearchSelect(item)}
                      className="search-modal-item"
                    >
                      <span className="item-icon">{item.icon}</span>
                      <span className="item-label">{item.label}</span>
                      {item.shortcut && (
                        <span className="item-shortcut">{item.shortcut}</span>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="search-no-results">
                    {t('admin_search_no_results')} "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLayout;
